import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import { Mail, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";

import { postContact } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { usePublicConfig } from "@/lib/usePublicConfig";

export default function Contact() {
  const { toast } = useToast();
  const cfg = usePublicConfig();
  const recaptchaSiteKey = cfg?.recaptcha_site_key || "";
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!recaptchaSiteKey) return;
    if (document.querySelector("script[data-recaptcha]")) return;
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    s.async = true; s.defer = true;
    s.setAttribute("data-recaptcha", "1");
    document.body.appendChild(s);
  }, [recaptchaSiteKey]);

  function setField(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  async function getRecaptchaToken() {
    if (!recaptchaSiteKey || !window.grecaptcha) return null;
    return new Promise((resolve) => {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(recaptchaSiteKey, { action: "contact" });
          resolve(token);
        } catch (e) { resolve(null); }
      });
    });
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const recaptcha_token = await getRecaptchaToken();
      await postContact({ ...form, consent, recaptcha_token });
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast({
        title: "Niet verzonden",
        description: err?.response?.data?.detail || "Er ging iets mis. Probeer opnieuw.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="py-16" data-testid="contact-page">
      <Seo path="/contact" />
      <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">Contact</span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-ink mt-3 tracking-tight">
            Vragen, opmerkingen of pers?
          </h1>
          <p className="text-ink-soft mt-5 leading-relaxed">
            We antwoorden gemiddeld binnen 2 werkdagen. Voor dringende technische problemen, gebruik het formulier en vermeld "URGENT" in het onderwerp.
          </p>

          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex items-start gap-3 text-ink-soft">
              <Mail className="h-5 w-5 text-[#002FA7] mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-subtle">E-mail</div>
                <a href="mailto:info@checkexpert.be" className="text-ink font-semibold">info@checkexpert.be</a>
              </div>
            </li>
            <li className="flex items-start gap-3 text-ink-soft">
              <MapPin className="h-5 w-5 text-[#002FA7] mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-subtle">Adres</div>
                Webtron BV<br />
                A. Rodenbachwijk 12, 8792 Waregem, België
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="rounded-3xl border border-border bg-white p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-[#10B981] mx-auto" />
              <h2 className="font-display text-2xl font-bold text-ink mt-4">Bericht ontvangen</h2>
              <p className="text-ink-soft mt-2">We mailen je binnen 2 werkdagen.</p>
              <button
                onClick={() => setDone(false)}
                className="mt-6 px-5 py-2.5 rounded-full bg-ink text-white text-sm font-semibold"
              >
                Nog een bericht
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-border bg-white p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Naam</Label>
                  <Input
                    id="name" required minLength={2}
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email" type="email" required
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="subject">Onderwerp</Label>
                <Input
                  id="subject" required minLength={2}
                  value={form.subject}
                  onChange={(e) => setField("subject", e.target.value)}
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <Label htmlFor="message">Bericht</Label>
                <Textarea
                  id="message" required minLength={5} rows={6}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  data-testid="contact-message"
                />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent" checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  data-testid="contact-consent"
                />
                <label htmlFor="consent" className="text-xs text-ink-soft leading-relaxed">
                  Ik ga akkoord dat CheckExpert mijn e-mail gebruikt om mijn vraag te beantwoorden. Geen marketing. Zie <a href="/privacy" className="text-[#002FA7] font-semibold">privacy</a>.
                </label>
              </div>
              <button
                type="submit"
                disabled={submitting || !consent}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Send className="h-4 w-4" /> {submitting ? "Verzenden…" : "Verstuur bericht"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
